import React from 'react';
import { Badge, Skeleton } from '@/components/ui';
import { UserCard } from './UserCard';
import type { User } from '@/types/userManagement.types';

interface UserTableProps {
  users: User[];
  onEdit?: (user: User) => void;
  onDelete?: (user: User) => void;
  onResendInvite?: (user: User) => void;
  isLoading?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: 'name' | 'email' | 'status' | 'companies' | 'groups' | 'lastLoginAt') => void;
}

type UserSortField = 'name' | 'email' | 'status' | 'companies' | 'groups' | 'lastLoginAt';

/**
 * Sortable header component with visual sort indicators
 */
const SortableHeader: React.FC<{
  field: UserSortField;
  label: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: UserSortField) => void;
  className?: string;
}> = ({ field, label, sortField, sortDirection, onSort, className = '' }) => {
  const isSorted = sortField === field;
  const ariaSort = isSorted ? (sortDirection === 'asc' ? 'ascending' : 'descending') : 'none';
  const isRight = className.includes('text-right');

  return (
    <th
      className={`px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300 md:px-6 md:py-3 ${className}`}
      aria-sort={onSort ? ariaSort : undefined}
    >
      {onSort ? (
        <button
          type="button"
          className={`flex w-full select-none items-center gap-1 rounded px-1 py-1 text-left uppercase tracking-wider hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-dark-600 ${isRight ? 'justify-end' : ''}`}
          onClick={() => onSort(field)}
          aria-label={`Sort by ${label}`}
        >
          <span>{label}</span>
          <div className="flex flex-col" aria-hidden="true">
            <svg
              className={`h-3 w-3 transition-colors ${
                isSorted && sortDirection === 'asc' ? 'text-primary-600' : 'text-gray-400'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" />
            </svg>
            <svg
              className={`-mt-1 h-3 w-3 transition-colors ${
                isSorted && sortDirection === 'desc' ? 'text-primary-600' : 'text-gray-400'
              }`}
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
            </svg>
          </div>
        </button>
      ) : (
        <span>{label}</span>
      )}
    </th>
  );
};

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' });
}

export const UserTable: React.FC<UserTableProps> = (props) => {
  const { users, onEdit, onDelete, onResendInvite, isLoading = false } = props;

  if (isLoading) {
    return (
      <>
        {/* Mobile loading skeleton — card shaped */}
        <div className="block space-y-2 md:hidden">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={`card-skeleton-${index}`}
              className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 dark:border-dark-700 dark:bg-dark-800"
            >
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 flex-shrink-0 rounded" />
                <Skeleton className="h-9 w-9 flex-shrink-0 rounded-full" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-full max-w-[200px]" />
                  <div className="flex flex-wrap gap-1">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop loading skeleton — table shaped */}
        <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800 md:block">
          <div className="space-y-1 p-4">
            <div className="grid grid-cols-12 gap-3 rounded-md bg-gray-50 p-3 dark:bg-dark-700">
              <Skeleton className="col-span-1 h-4 w-4" />
              <Skeleton className="col-span-3 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-2 h-4" />
              <Skeleton className="col-span-2 h-4" />
            </div>
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`table-user-skeleton-${index}`} className="grid grid-cols-12 gap-3 p-3">
                <Skeleton className="col-span-1 h-4 w-4" />
                <div className="col-span-3 flex items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
                <Skeleton className="col-span-2 h-6 w-20 rounded-full" />
                <Skeleton className="col-span-2 h-6 w-24 rounded-full" />
                <Skeleton className="col-span-2 h-6 w-24 rounded-full" />
                <div className="col-span-2 flex justify-end gap-2">
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                  <Skeleton className="h-8 w-8 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (users.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-dark-700 dark:bg-dark-800">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 48 48"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h10a2 2 0 000-4H17a2 2 0 000 4zm0-8h14a2 2 0 000-4H17a2 2 0 000 4zm0 16h14a2 2 0 000-4H17a2 2 0 000 4z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No users found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Get started by creating a new user.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile — card list */}
      <div className="block space-y-2 md:hidden">
        {users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onEdit={onEdit}
            onDelete={onDelete}
            onResendInvite={onResendInvite}
          />
        ))}
      </div>

      {/* Desktop — table (unchanged) */}
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-800 md:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] table-fixed divide-y divide-gray-200 dark:divide-gray-700">
            <colgroup>
              <col className="min-w-0" style={{ width: '22%' }} />
              <col style={{ width: '6.75rem' }} />
              <col className="min-w-0" style={{ width: '20%' }} />
              <col className="hidden min-w-0 lg:table-column" style={{ width: '20%' }} />
              <col style={{ width: '7rem' }} />
              <col style={{ width: '5.5rem' }} />
            </colgroup>
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <SortableHeader
                  field="name"
                  label="User"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                />
                <SortableHeader
                  field="status"
                  label="Status"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                />
                <SortableHeader
                  field="companies"
                  label="Companies"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                />
                <SortableHeader
                  field="groups"
                  label="Roles"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden lg:table-cell"
                />
                <SortableHeader
                  field="lastLoginAt"
                  label="Last Login"
                  sortField={props.sortField}
                  sortDirection={props.sortDirection}
                  onSort={props.onSort}
                  className="hidden !text-right sm:table-cell"
                />
                <th className="px-1.5 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-700 dark:text-gray-300 md:py-3 md:pl-2 md:pr-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
              {users.map((user) => (
                <tr key={user.id} className="group hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="min-w-0 px-2 py-2 md:px-3 md:py-2.5">
                    <div className="flex min-w-0 items-center">
                      <div className="h-8 w-8 flex-shrink-0 md:h-9 md:w-9">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-300 dark:bg-gray-600 md:h-9 md:w-9">
                          <span className="text-xs font-medium text-gray-700 dark:text-gray-300 md:text-sm">
                            {user.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-2 min-w-0 md:ml-3">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-white">
                          {user.name}
                        </div>
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400 md:text-sm">
                          {user.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-2 py-2 md:px-3 md:py-2.5">
                    <Badge
                      variant={
                        user.inviteStatus === 'Accepted'
                          ? 'success'
                          : user.inviteStatus === 'Pending'
                            ? 'warning'
                            : user.inviteStatus === 'Declined'
                              ? 'error'
                              : 'default'
                      }
                      size="sm"
                    >
                      {user.inviteStatus}
                    </Badge>
                  </td>
                  <td className="min-w-0 px-2 py-2 align-top md:px-3 md:py-2.5">
                    <div className="flex flex-wrap content-start gap-x-1 gap-y-1 text-sm text-gray-900 dark:text-white">
                      {user.companies.length > 0 ? (
                        <>
                          {user.companies.slice(0, 5).map((company) => (
                            <span
                              key={company.id}
                              title={company.name}
                              className="inline-flex min-w-0 max-w-[9.5rem] shrink"
                            >
                              <Badge
                                variant="primary"
                                size="sm"
                                className="min-w-0 max-w-full truncate"
                              >
                                {company.name}
                              </Badge>
                            </span>
                          ))}
                          {user.companies.length > 5 && (
                            <span className="self-center text-xs text-gray-500 dark:text-gray-400">
                              +{user.companies.length - 5} more
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">None</span>
                      )}
                    </div>
                  </td>
                  <td className="hidden min-w-0 px-2 py-2 align-top md:px-3 md:py-2.5 lg:table-cell">
                    <div className="flex flex-wrap content-start gap-x-1 gap-y-1 text-sm text-gray-900 dark:text-white">
                      {(() => {
                        const companyIcon = (
                          <svg viewBox="0 0 20 20" fill="currentColor" className="h-3 w-3">
                            <path
                              fillRule="evenodd"
                              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z"
                              clipRule="evenodd"
                            />
                          </svg>
                        );
                        const chips: {
                          key: string;
                          name: string;
                          variant: 'default' | 'primary';
                          icon?: React.ReactNode;
                        }[] = [
                          ...user.userGroups.map((g) => ({
                            key: `u-${g.id}`,
                            name: g.name,
                            variant: 'default' as const,
                          })),
                          ...(user.companyUserGroups ?? []).map((g) => ({
                            key: `c-${g.id}`,
                            name: g.name,
                            variant: 'primary' as const,
                            icon: companyIcon,
                          })),
                        ];
                        if (chips.length === 0) {
                          return <span className="text-gray-400 dark:text-gray-500">None</span>;
                        }
                        return (
                          <>
                            {chips.slice(0, 5).map((c) => (
                              <span
                                key={c.key}
                                title={c.name}
                                className="inline-flex min-w-0 max-w-[9.5rem] shrink"
                              >
                                <Badge
                                  variant={c.variant}
                                  size="sm"
                                  icon={c.icon}
                                  className="min-w-0 max-w-full truncate"
                                >
                                  {c.name}
                                </Badge>
                              </span>
                            ))}
                            {chips.length > 5 && (
                              <span className="self-center text-xs text-gray-500 dark:text-gray-400">
                                +{chips.length - 5} more
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </td>
                  <td className="hidden whitespace-nowrap px-2 py-2 text-right text-sm text-gray-700 dark:text-gray-300 sm:table-cell md:px-3 md:py-2.5">
                    {formatDateTime(user.lastLoginAt)}
                  </td>
                  <td className="whitespace-nowrap px-1.5 py-2 text-right align-top text-sm font-medium md:px-2 md:py-2.5">
                    <div className="flex items-center justify-end gap-0.5">
                      {onEdit && (
                        <button
                          type="button"
                          onClick={() => onEdit(user)}
                          className="rounded p-1 text-blue-600 hover:bg-blue-50 hover:text-blue-900 dark:text-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-300"
                          title="Edit user"
                          aria-label={`Edit ${user.name}`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                        </button>
                      )}

                      {onResendInvite && user.inviteStatus === 'Pending' && (
                        <button
                          type="button"
                          onClick={() => onResendInvite(user)}
                          className="rounded p-1 text-green-600 hover:bg-green-50 hover:text-green-900 dark:text-green-400 dark:hover:bg-green-900/20 dark:hover:text-green-300"
                          title="Resend invitation"
                          aria-label={`Resend invitation to ${user.name}`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                          </svg>
                        </button>
                      )}

                      {onDelete && (
                        <button
                          type="button"
                          onClick={() => onDelete(user)}
                          className="rounded p-1 text-red-600 hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300"
                          title="Delete user"
                          aria-label={`Delete ${user.name}`}
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};
